"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _TabBarItem = _interopRequireDefault(require("./TabBarItem"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const selectTabBarContainerStyles = _ref => {
  let {
    paddingTop,
    paddingBottom,
    borderTopWidth,
    borderTopColor,
    backgroundColor
  } = _ref;
  return {
    paddingTop,
    paddingBottom,
    borderTopWidth,
    borderTopColor,
    backgroundColor
  };
};
const selectTabBarItemContainerStyles = _ref2 => {
  let {
    paddingLeft,
    paddingRight,
    gap
  } = _ref2;
  return {
    paddingLeft,
    paddingRight,
    gap
  };
};
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);

/**
 * TabBar component renders a navigation bar with multiple TabBarItems.
 * It allows users to switch between different views or sections.
 *
 * @component
 * @example
 * const items = [
 *   { id: '1', label: 'Home', icon: <HomeIcon />, iconActive: <HomeActiveIcon /> },
 *   { id: '2', label: 'Profile', icon: <ProfileIcon />, iconActive: <ProfileActiveIcon /> },
 * ]
 * return (
 *   <TabBar
 *     items={items}
 *     initiallySelectedItem="1"
 *     onChange={(itemId) => console.log(itemId)}
 *   />
 * )
 */

const TabBar = /*#__PURE__*/_react.default.forwardRef((_ref3, ref) => {
  let {
    items = [],
    initiallySelectedItem = '0',
    onChange,
    variant,
    tokens,
    ...rest
  } = _ref3;
  const [isSelected, setIsSelected] = _react.default.useState(initiallySelectedItem);
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('TabBar', tokens, variant);
  const handlePress = itemId => {
    setIsSelected(itemId);
    onChange?.(itemId);
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    ref: ref,
    style: [styles.tabBar, selectTabBarContainerStyles(themeTokens)],
    ...selectProps(rest),
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
      style: [styles.tabBarItem, selectTabBarItemContainerStyles(themeTokens)],
      children: items.map((item, index) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_TabBarItem.default, {
        label: item.label,
        href: item.href,
        isSelected: isSelected === item.id,
        icon: item.icon,
        iconActive: item.iconActive,
        onPress: () => handlePress(item.id),
        id: `tab-item-${index}`,
        accessibilityRole: "tablist"
      }, item.id))
    })
  });
});
TabBar.displayName = 'TabBar';
TabBar.propTypes = {
  ...selectedSystemPropTypes,
  /** Array of TabBarItems to be displayed in TabBar */
  items: _propTypes.default.arrayOf(_propTypes.default.shape({
    id: _propTypes.default.string.isRequired,
    icon: _propTypes.default.node,
    iconActive: _propTypes.default.node,
    label: _propTypes.default.string.isRequired,
    href: _propTypes.default.string
  })).isRequired,
  /** Id of the initially selected item. */
  initiallySelectedItem: _propTypes.default.number,
  /** Callback function to handle item selection change. */
  onChange: _propTypes.default.func,
  /** Variant of TabBar for styling purposes. */
  variant: _utils.variantProp.propType,
  /** Tokens for theming and styling. */
  tokens: (0, _utils.getTokensPropType)('TabBar')
};
const styles = _StyleSheet.default.create({
  tabBar: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  tabBarItem: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  }
});
var _default = exports.default = TabBar;